import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Compass, X, ArrowRight, ArrowLeft, Volume2, VolumeX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const track = (action, step) => {
  const leadId = localStorage.getItem('demo_lead_id');
  axios.post(`${API}/demo/tour-event`, { action, step, ...(leadId ? { lead_id: leadId } : {}) }).catch(() => {});
};

const STEP_SETS = {
  gym: [
    { path: '/', title: 'The Dashboard', body: 'Your command centre. Every tab here is powered by real (demo) call data — start with the Revivable Deals tab up top.' },
    { path: '/', title: 'Revivable Deals + Morning Calls', body: 'Open the Revivable Deals tab: the 6am CRM call list sits on top, then the SMS Reply Inbox with its 15-minute countdown, then the AI-ranked deals worth calling back.' },
    { path: '/sms-inbox', title: 'SMS Inbox', body: 'Every SMS sent and received, searchable. Reps see only their own messages; managers see everything. Reply straight from a row with one-tap templates.' },
    { path: '/club-summary', title: 'Summary Report', body: 'The monthly report rebuilt from actual transcripts — appointments, close ratio, revival rate, and new callers by lead source.' },
    { path: '/calls', title: 'Graded Calls', body: 'Open any call: full transcript, AI grading against your criteria, coaching feedback and one-tap SMS playbooks.' },
    { path: '/manager/ops', title: 'Manager Ops', body: 'Per-rep performance, response-time slippage flags and coaching records — everything a supervisor needs each morning. That\u2019s the tour!' },
  ],
  carsales: [
    { path: '/', title: 'The Dealership Dashboard', body: 'Every sales call at Velocity Motors, graded minutes after your salesperson hangs up. Start with the Revivable Deals tab up top.' },
    { path: '/', title: 'Revivable Deals', body: 'Test drives that never got booked, trade-ins that went quiet — the AI ranks which prospects are worth calling back today, with the exact reason.' },
    { path: '/sms-inbox', title: 'SMS Inbox', body: 'Every message between your team and buyers, with a 15-minute reply countdown so no enquiry goes cold while a competitor calls them back first.' },
    { path: '/calls', title: 'Graded Calls', body: 'Open any call: full transcript scored against your sales playbook — test-drive close, trade-in valuation, finance mention, follow-up commitment.' },
    { path: '/manager/ops', title: 'Sales Manager Ops', body: 'Per-salesperson close attempts, response-time slippage and coaching records — your Monday sales meeting, pre-built. That\u2019s the tour!' },
  ],
  helpdesk: [
    { path: '/', title: 'The Service Desk Dashboard', body: 'Every support call at Apex IT, transcribed and scored minutes after the engineer hangs up. Start with the Revivable Deals tab up top.' },
    { path: '/', title: 'At-Risk Accounts', body: 'Frustrated callers, unresolved tickets and renewal risks — the AI ranks which clients need a call-back today, before they start shopping around.' },
    { path: '/sms-inbox', title: 'SMS Inbox', body: 'Every status update and client reply in one place, with a 15-minute response countdown so nobody is left waiting on an outage.' },
    { path: '/calls', title: 'Graded Calls', body: 'Open any call: full transcript scored against your service standards — ownership taken, clear ETA given, resolution confirmed, escalation handled.' },
    { path: '/manager/ops', title: 'Service Manager Ops', body: 'Per-engineer scores, response-time slippage and coaching records — your service review, pre-built. That\u2019s the tour!' },
  ],
};

export const DemoTour = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDemo = (user?.domain_id || '').startsWith('demo-');
  const variant = user?.domain_id === 'demo-vmg' ? 'carsales' : user?.domain_id === 'demo-apex' ? 'helpdesk' : 'gym';
  const STEPS = STEP_SETS[variant];
  const [step, setStep] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('demo_tour_voice') !== 'off');
  const audioRef = useRef(null);

  const stopVoice = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  };

  useEffect(() => {
    stopVoice();
    if (step === null || !voiceOn) return;
    const a = new Audio(`${API}/demo/tour-audio/${variant}/${step}.mp3`);
    audioRef.current = a;
    a.play().catch(() => {});
    return stopVoice;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, voiceOn, variant]);

  const toggleVoice = () => {
    const next = !voiceOn;
    localStorage.setItem('demo_tour_voice', next ? 'on' : 'off');
    setVoiceOn(next);
    if (!next) stopVoice();
  };

  useEffect(() => {
    if (isDemo && !localStorage.getItem('demo_tour_done')) {
      const saved = localStorage.getItem('demo_tour_step');
      setStep(Number(saved || 0));
      if (saved === null) track('view', 0);
    }
  }, [isDemo]);

  if (!isDemo) return null;

  const end = (action, atStep) => {
    stopVoice();
    track(action, atStep);
    localStorage.setItem('demo_tour_done', '1');
    localStorage.removeItem('demo_tour_step');
    setStep(null);
  };
  const close = () => end('close', step ?? 0);
  const go = (n) => {
    const next = step + n;
    if (next >= STEPS.length) return end('finish', step);
    if (next < 0) return end('close', step);
    localStorage.setItem('demo_tour_step', String(next));
    if (n > 0) track('view', next);
    setStep(next);
    if (STEPS[next].path !== location.pathname) navigate(STEPS[next].path);
  };

  if (step === null) {
    return (
      <button onClick={() => { localStorage.removeItem('demo_tour_done'); localStorage.setItem('demo_tour_step', '0'); track('view', 0); setStep(0); navigate(STEPS[0].path); }}
        className="fixed bottom-5 right-5 z-50 bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold px-4 py-2.5 rounded-full shadow-lg inline-flex items-center gap-1.5"
        data-testid="demo-tour-restart">
        <Compass className="w-4 h-4" /> Take the tour
      </button>
    );
  }

  const s = STEPS[step];
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[340px] bg-slate-900 text-white rounded-2xl shadow-2xl border border-teal-500/40 p-5" data-testid="demo-tour-card">
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold text-teal-300 flex items-center gap-1.5"><Compass className="w-4 h-4" /> Demo tour · {step + 1}/{STEPS.length}</p>
        <div className="flex items-center gap-1">
          <button onClick={toggleVoice} className="text-white/40 hover:text-white" title={voiceOn ? 'Mute narration' : 'Unmute narration'} data-testid="demo-tour-voice-toggle">
            {voiceOn ? <Volume2 className="w-4 h-4 text-teal-300" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button onClick={close} className="text-white/40 hover:text-white" data-testid="demo-tour-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <p className="font-bold text-lg mt-2" data-testid="demo-tour-title">{s.title}</p>
      <p className="text-sm text-white/65 mt-1 leading-relaxed">{s.body}</p>
      <div className="flex items-center justify-between mt-4">
        <div className="flex gap-1">
          {STEPS.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? 'bg-teal-400' : 'bg-white/20'}`} />)}
        </div>
        <div className="flex gap-2">
          {step > 0 && (
            <button onClick={() => go(-1)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center gap-1" data-testid="demo-tour-back">
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
          )}
          <button onClick={() => go(1)} className="text-xs font-bold px-3 py-1.5 rounded-full bg-teal-400 text-slate-950 hover:bg-teal-300 inline-flex items-center gap-1" data-testid="demo-tour-next">
            {step === STEPS.length - 1 ? 'Finish' : 'Next'} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};
